<!-- Invoice Export Certificate Display -->
<div class="invoice-export-certificate-container">

  <!-- Invoice Header (Optional) -->
  <div *ngIf="showInvoiceHeader" class="invoice-header">
    <span class="inv-number">Invoice: {{ invoice.invoice_number }}</span>
    <span class="inv-vehicle">{{ invoice.vehicle.make }} {{ invoice.vehicle.model }}</span>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="d-flex justify-content-center align-items-center py-10">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading certificate...</span>
    </div>
    <span class="ms-3 text-muted">Loading export certificate...</span>
  </div>

  <!-- Error State -->
  <div *ngIf="hasError && !isLoading" class="alert alert-warning d-flex align-items-center py-5" role="alert">
    <i class="ki-duotone ki-information-5 fs-2hx text-warning me-4">
      <span class="path1"></span>
      <span class="path2"></span>
      <span class="path3"></span>
    </i>
    <div class="d-flex flex-column">
      <h4 class="mb-1 text-dark">Export Certificate Not Available</h4>
      <span>{{ errorMessage }}</span>
    </div>
  </div>

  <!-- Certificate Content -->
  <div *ngIf="!isLoading && !hasError && certificate" class="cert-page">

    <!-- Title -->
    <div class="cert-title">
      <h1>EXPORT CERTIFICATE</h1>
    </div>

    <!-- Certificate Meta Info -->
    <div class="cert-meta">
      <div>CERTIFICATE NO. {{ certificate.certificate_no || '' }}</div>
      <div>REFERENCE NUMBER: {{ certificate.reference_number || '' }}</div>
    </div>

    <!-- Main Table (11 columns) -->
    <div class="cert-table-wrapper">
      <table class="cert-table">

        <!-- Row 1: Registration header labels -->
        <tr>
          <td class="c-lbl" colspan="3">Registration No.</td>
          <td class="c-lbl" colspan="3">Registration Date</td>
          <td class="c-lbl" colspan="2">First Reg. Date</td>
          <td class="c-lbl" colspan="3">Maker's serial number</td>
        </tr>

        <!-- Row 2: Registration data -->
        <tr>
          <td class="c-val c-lg" colspan="3">{{ certificate.registration_no || '' }}</td>
          <td class="c-val c-lg" colspan="3">{{ certificate.registration_date || '' }}</td>
          <td class="c-val c-lg" colspan="2">{{ certificate.first_reg_date || '' }}</td>
          <td class="c-val c-lg" colspan="3">{{ certificate.makers_serial_number || invoice.vehicle.chassis_no || '' }}</td>
        </tr>

        <!-- Row 3: Trademark / Model / Engine headers -->
        <tr>
          <td class="c-lbl" colspan="4">Trademark of the maker of the vehicle</td>
          <td class="c-lbl" colspan="3">Model</td>
          <td class="c-lbl" colspan="2">Engine Model</td>
          <td class="c-lbl" colspan="1">Engine Number</td>
          <td class="c-lbl" colspan="1">Fuel</td>
        </tr>

        <!-- Row 4: Trademark / Model / Engine data -->
        <tr>
          <td class="c-val c-lg" colspan="4">{{ certificate.trademark_maker || invoice.vehicle.make || '' }}</td>
          <td class="c-val c-lg" colspan="3">{{ certificate.model || invoice.vehicle.model || '' }}</td>
          <td class="c-val c-lg" colspan="2">{{ certificate.engine_model || invoice.vehicle.engine || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.vehicle?.engine_no || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.classification_of_fuel || invoice.vehicle.fuel_type || '' }}</td>
        </tr>

        <!-- Row 5: Name of Owner -->
        <tr>
          <td class="c-lbl c-left" colspan="3">Name of Owner</td>
          <td class="c-val c-left" colspan="8">{{ certificate.name_of_owner || '' }}</td>
        </tr>

        <!-- Row 6: Address of Owner -->
        <tr>
          <td class="c-lbl c-left" colspan="3">Address of Owner</td>
          <td class="c-val c-left" colspan="8">{{ certificate.address_of_owner || '' }}</td>
        </tr>

        <!-- Row 7: Name of User -->
        <tr>
          <td class="c-lbl c-left" colspan="3">Name of User</td>
          <td class="c-val c-left" colspan="8">{{ certificate.name_of_user || '' }}</td>
        </tr>

        <!-- Row 8: Address of User -->
        <tr>
          <td class="c-lbl c-left" colspan="3">Address of User</td>
          <td class="c-val c-left" colspan="8">{{ certificate.address_of_user || '' }}</td>
        </tr>

        <!-- Row 9: Locality of principal abode of use -->
        <tr>
          <td class="c-lbl c-left" colspan="3">Locality of principal abode of use</td>
          <td class="c-val c-left" colspan="8">{{ certificate.locality_principal_abode || '' }}</td>
        </tr>

        <!-- Row 10: Classification headers -->
        <tr>
          <td class="c-lbl" colspan="2">Classification of vehicle</td>
          <td class="c-lbl" colspan="1">Use</td>
          <td class="c-lbl" colspan="1">Purpose</td>
          <td class="c-lbl" colspan="1">Type of Body</td>
          <td class="c-lbl" colspan="1">Fixed Number</td>
          <td class="c-lbl" colspan="1">Maxim Carry</td>
          <td class="c-lbl" colspan="2">Weight</td>
          <td class="c-lbl" colspan="2">G/Weight</td>
        </tr>

        <!-- Row 11: Classification data -->
        <tr>
          <td class="c-val c-lg" colspan="2">{{ certificate.classification_of_vehicle || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.use || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.purpose || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.type_of_body || invoice.vehicle.body_type || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.fixed_number || '' }}</td>
          <td class="c-val c-lg" colspan="1">{{ certificate.maximum_carry || '' }}</td>
          <td class="c-val c-lg" colspan="2">{{ certificate.weight || '' }}</td>
          <td class="c-val c-lg" colspan="2">{{ certificate.g_weight || '' }}</td>
        </tr>

        <!-- Row 12: Engine / Dimension headers -->
        <tr>
          <td class="c-lbl">Engine Capacity</td>
          <td class="c-lbl">Classification of fuel</td>
          <td class="c-lbl">Specification No.</td>
          <td class="c-lbl">Classification No.</td>
          <td class="c-lbl">Length</td>
          <td class="c-lbl">Width</td>
          <td class="c-lbl">Height</td>
          <td class="c-lbl">FF Weight</td>
          <td class="c-lbl">FR Weight</td>
          <td class="c-lbl">RF Weight</td>
          <td class="c-lbl">RR Weight</td>
        </tr>

        <!-- Row 13: Engine / Dimension data -->
        <tr>
          <td class="c-val c-lg">{{ certificate.engine_capacity || '' }}</td>
          <td class="c-val c-lg">{{ certificate.classification_of_fuel || invoice.vehicle.fuel_type || '' }}</td>
          <td class="c-val c-lg">{{ certificate.specification_no || '' }}</td>
          <td class="c-val c-lg">{{ certificate.classification_no || '' }}</td>
          <td class="c-val c-lg">{{ certificate.length || '' }}</td>
          <td class="c-val c-lg">{{ certificate.width || '' }}</td>
          <td class="c-val c-lg">{{ certificate.height || '' }}</td>
          <td class="c-val c-lg">{{ certificate.f_weight || '' }}</td>
          <td class="c-val c-lg">{{ certificate.fr_weight || '' }}</td>
          <td class="c-val c-lg">{{ certificate.rf_weight || '' }}</td>
          <td class="c-val c-lg">{{ certificate.r_weight || '' }}</td>
        </tr>

        <!-- Row 14: Export Scheduled Day -->
        <tr>
          <td class="c-lbl c-left" colspan="3">Export Scheduled Day</td>
          <td class="c-val c-left" colspan="8">{{ certificate.export_scheduled_day || '' }}</td>
        </tr>

        <!-- Row 15: Remarks + Authorized Signature -->
        <tr>
          <td class="c-remarks" colspan="7">
            <div class="remarks-heading">REMARKS</div>
            <div class="remarks-body" *ngIf="certificate.remarks">{{ certificate.remarks }}</div>
            <div class="remarks-owner"
                 *ngIf="certificate.latest_owner_name_registration || certificate.old_vehicle_registration_no">
              <div *ngIf="certificate.old_vehicle_registration_no">{{ certificate.old_vehicle_registration_no }}</div>
              <div *ngIf="certificate.latest_owner_name_registration">
                <br>Latest Owner<br>{{ certificate.latest_owner_name_registration }}
              </div>
              <div *ngIf="certificate.date_of_application">
                <br>Date of Application {{ certificate.date_of_application }}
              </div>
            </div>
          </td>
          <td class="c-signature" colspan="4">
            <div class="sig-label">AUTHORIZED SIGNATURE(S)</div>
            <div class="sig-stamp">
              <img *ngIf="settings?.logo" [src]="settings.logo" alt="Company Stamp" class="stamp-img">
              <div *ngIf="!settings?.logo" class="stamp-box">[STAMP]</div>
            </div>
          </td>
        </tr>

      </table>
    </div>

    <!-- Footer (outside table) -->
    <div class="cert-footer">
      <div class="footer-date">DATE: {{ certificate.final_date || formatDate(null) }}</div>
      <div class="footer-authority">
        DIRECTOR GENERAL OF THE DISTRICT TRANSPORT BUREAU OR DIRECTOR OF THE TRANSPORT
        BRANCH OF THE DISTRICT TRANSPORT BUREAU. MINISTRY OF LAND, INFRASTRUCTURE,
        TRANSPORT AND TOURISM, JAPAN
      </div>
      <div class="footer-governor">Governor of: {{ settings?.city || 'KOBE' }}</div>
    </div>

  </div>

</div>

